import {isEqual} from 'lodash';
import {RefObject, useEffect, useState} from 'react';

import {
  findAncestorByTag,
  findAncestorByTags,
  isElement,
  isTableCellElement,
  isTableElement,
  isTableRowElement,
} from '../../../../../../../libs/shared/core/src/helpers/browser/domHelpers';
import {useOnSelectionChange} from '../../../../../common/annotation/useOnSelectionChange';

/*
 * Constants.
 */

export const tableCellSelectedClass = 'front-table-cell-selected';
export const emailViewerTableSelectionClass = 'front-email-viewer-selectable-table';

/*
 * Types.
 */

interface TableCell {
  row: number;
  cell: number;
}

interface TableRange {
  start: TableCell;
  end: TableCell;
}

interface TableSelection {
  range: TableRange;
  // 2d array of cells - we maintain the original rows to build the paste payload.
  cellsInRange: Array<Array<HTMLTableCellElement>>;
  table: HTMLTableElement;
}

/*
 * Hook.
 */

/**
 * A hook for handling custom selections in tables. Needed for column selections - since
 * chromium doesn't natively support non-contiguous selections.
 *
 * @param wrapperRef A ref to a div wrapping the area you want the table selection logic to run on.
 */
export const useTableSelection = (wrapperRef: RefObject<HTMLDivElement>) => {
  const [previousSelection, setPreviousSelection] = useState<TableSelection | undefined>();

  /*
   * Event handlers.
   */

  const clearTableSelection = () => {
    if (previousSelection) {
      removeSelectionStyle(previousSelection);
    }
    setPreviousSelection(undefined);
  };

  const onSelectionChange = (selection: Selection | null) => {
    const tableRange = computeTableRange(selection, wrapperRef.current);

    if (!tableRange) {
      clearTableSelection();
      return;
    }

    if (tableRange.table === previousSelection?.table && isEqual(tableRange.range, previousSelection.range)) {
      // This is the same selection, nothing to do.
      return;
    }

    // The selection has changed, clear the old one so we can select new cells.
    clearTableSelection();

    const tableCellsInRange = findCellsInRange(tableRange.table, tableRange.range);

    if (tableCellsInRange.length < 1) {
      return;
    }

    const tableSelection = {...tableRange, cellsInRange: tableCellsInRange};

    styleSelection(tableSelection);
    setPreviousSelection(tableSelection);
  };

  /*
   * Event Listeners.
   */

  useOnSelectionChange({targetRef: wrapperRef, onSelectionChange});

  useEffect(() => {
    if (!previousSelection) {
      return undefined;
    }

    const doc = wrapperRef.current?.ownerDocument;
    if (!doc) {
      return undefined;
    }

    const listener = (e: ClipboardEvent) => {
      copyToClipboard(e, previousSelection);
    };

    doc.addEventListener('copy', listener);

    return () => {
      doc.removeEventListener('copy', listener);
    };
  }, [previousSelection, wrapperRef]);
};

/*
 * Helpers.
 */

const computeTableRange = (selection: Selection | null, container: HTMLDivElement | null) => {
  if (!container) {
    return undefined;
  }

  if (!selection || selection.isCollapsed) {
    return undefined;
  }

  const startCell = findCellElementFromNode(selection.anchorNode, container);
  const endCell = findCellElementFromNode(selection.focusNode, container);

  if (!startCell || !endCell || startCell === endCell) {
    return undefined;
  }

  const startTable = findParentTableFromCell(startCell, container);
  const endTable = findParentTableFromCell(endCell, container);
  if (!startTable || !endTable || startTable !== endTable) {
    return undefined;
  }

  // Only run the custom selection handling on top level tables, if we are
  // in a nested table fallback on the default selection.
  const isNestedTable = startTable.parentElement
    ? findAncestorByTag(startTable.parentElement, container, 'TABLE') !== null
    : false;
  if (isNestedTable) {
    return undefined;
  }

  const range = computeRangeByStartAndEndCell(startCell, endCell, container);
  if (!range) {
    return undefined;
  }

  return {table: startTable, range};
};

const findCellElementFromNode = (inputNode: Node | null, container: Element) => {
  // Get the parent element from the selection, which may be in a text node
  const inputElement = isElement(inputNode) ? inputNode : inputNode?.parentElement;
  if (!inputElement) {
    return undefined;
  }

  const parentCell = findAncestorByTags(inputElement, container, ['TD', 'TH']);

  if (isTableCellElement(parentCell)) {
    return parentCell;
  }

  return undefined;
};

const findParentTableFromCell = (cellElement: HTMLTableCellElement, container: Element) => {
  const parentTable = findAncestorByTag(cellElement, container, 'TABLE');

  if (isTableElement(parentTable)) {
    return parentTable;
  }

  return undefined;
};

const computeRangeByStartAndEndCell = (
  startCell: HTMLTableCellElement,
  endCell: HTMLTableCellElement,
  container: Element,
) => {
  const startRowElement = findAncestorByTag(startCell, container, 'TR');
  const endRowElement = findAncestorByTag(endCell, container, 'TR');

  if (
    !startRowElement ||
    !endRowElement ||
    !isTableRowElement(startRowElement) ||
    !isTableRowElement(endRowElement)
  ) {
    return undefined;
  }

  const range = {
    start: {
      row: startRowElement.rowIndex,
      cell: startCell.cellIndex,
    },
    end: {
      row: endRowElement.rowIndex,
      cell: endCell.cellIndex,
    },
  };

  // Flip start/end if start > end
  if (range.start.cell > range.end.cell) {
    [range.start.cell, range.end.cell] = [range.end.cell, range.start.cell];
  }
  if (range.start.row > range.end.row) {
    [range.start.row, range.end.row] = [range.end.row, range.start.row];
  }

  return range;
};

const findCellsInRange = (table: HTMLTableElement, range: TableRange) => {
  // First get the rows from our table in the selection range.
  const rowElements = Array.from(table.querySelectorAll<HTMLTableRowElement>('tr')).slice(
    range.start.row,
    range.end.row + 1,
  );

  // Then get the cells, as a 2d array for easy paste payload construction.
  const selectedCells = rowElements.map((row: HTMLTableRowElement) =>
    Array.from(row.querySelectorAll<HTMLTableCellElement>('td,th')).slice(
      range.start.cell,
      range.end.cell + 1,
    ),
  );

  return selectedCells;
};

const styleSelection = (selection: TableSelection) => {
  selection.table.classList.add(emailViewerTableSelectionClass);

  selection.cellsInRange.forEach((row) => {
    row.forEach((cellElement) => {
      cellElement.classList.add(tableCellSelectedClass);
    });
  });
};

const removeSelectionStyle = (previousSelection: TableSelection) => {
  previousSelection.table.classList.remove(emailViewerTableSelectionClass);

  previousSelection.cellsInRange.forEach((row) => {
    row.forEach((cellElement) => {
      cellElement.classList.remove(tableCellSelectedClass);
    });
  });
};

const copyToClipboard = (e: ClipboardEvent, previousSelection: TableSelection | undefined) => {
  if (!previousSelection || !e.clipboardData) {
    return;
  }

  const selectionText = previousSelection.cellsInRange
    .map((row) => row.map((cell) => cell.innerText).join('\t'))
    .join('\r\n');

  const selectionHtml = `<table>${previousSelection.cellsInRange
    .map(
      (row) =>
        `<tr>${row
          .map((cell) => {
            // Create a clone of the node so that we can strip the
            // selection class before adding the HTML to the payload.
            const clone = cell.cloneNode(true);
            // Shouldn't happen (but needed because return type above is `Node`)
            if (!isTableCellElement(clone)) {
              return undefined;
            }
            clone.classList.remove(tableCellSelectedClass);
            // Use the full HTML here to maintain styling.
            return `${clone.outerHTML}`;
          })
          .join('')}</tr>`,
    )
    .join('')}</table>`;

  if (!selectionText || !selectionHtml) {
    return;
  }

  e.clipboardData.setData('text/plain', selectionText);
  e.clipboardData.setData('text/html', selectionHtml);
  e.preventDefault();
};
